import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DiscussionDto } from '@workspace/data-types';
import { UserStore } from '@workspace/ng-data-access';

@Component({
  selector: 'core-discussion-sidebar',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
  ],
  host: {
    class: 'block h-full',
  },
  templateUrl: './discussion-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionSidebarCoreComponent {
  private readonly userStore = inject(UserStore);

  discussions = input<DiscussionDto[]>([]);
  activeDiscussion = input<DiscussionDto | null>(null);
  isLoading = input<boolean>(false);
  timeFormat = input<string>('HH:mm');

  // Dynamically configure empty states and translation/custom labels
  title = input<string>('Messages');
  searchPlaceholder = input<string>('Rechercher une discussion...');
  emptyText = input<string>('Aucune discussion trouvée');

  // Custom status template inside the avatar area (like candidacy statusAccepted check)
  avatarBadgeTemplate = input<TemplateRef<{ $implicit: DiscussionDto }> | null>(null);

  // Custom name/initial resolvers if business needs special mapping
  participantNameResolver = input<(discussion: DiscussionDto) => string>();
  participantInitialResolver = input<(discussion: DiscussionDto) => string>();

  discussionSelected = output<DiscussionDto>();
  markAsUnread = output<{ discussion: DiscussionDto; event: Event }>();
  toggleStar = output<{ discussion: DiscussionDto; event: Event }>();
  archiveDiscussion = output<{ discussion: DiscussionDto; event: Event }>();
  filterTabChanged = output<'all' | 'unread' | 'archived'>();
  scrolledToBottom = output<void>();

  readonly filterTab = signal<'all' | 'unread' | 'archived'>('all');
  readonly searchQuery = signal<string>('');

  readonly filteredDiscussions = computed(() => {
    let list = this.discussions() || [];
    const tab = this.filterTab();
    const query = this.searchQuery().toLowerCase().trim();

    // 1. Filter by tabs
    if (tab === 'unread') {
      list = list.filter((d) => this.getUnreadCount(d) > 0);
    } else if (tab === 'archived') {
      list = list.filter((d) => this.isDiscussionArchived(d));
    } else {
      // 'all' -> show non-archived discussions
      list = list.filter((d) => !this.isDiscussionArchived(d));
    }

    // 2. Filter by search query
    if (query) {
      list = list.filter((d) => {
        const name = this.getParticipantName(d).toLowerCase();
        const lastMsg = (d.lastMessageText || '').toLowerCase();
        return name.includes(query) || lastMsg.includes(query);
      });
    }

    return list;
  });

  selectTab(tab: 'all' | 'unread' | 'archived') {
    this.filterTab.set(tab);
    this.filterTabChanged.emit(tab);
  }

  getParticipantName(discussion: DiscussionDto): string {
    const resolver = this.participantNameResolver();
    if (resolver) return resolver(discussion);

    const userId = this.userStore.current()?._id;
    const other = discussion.participants.find(
      (p) => userId && p.participant.toString() !== userId.toString(),
    );
    if (other?.firstName) {
      return other.firstName;
    }
    return 'Utilisateur';
  }

  getParticipantInitial(discussion: DiscussionDto): string {
    const resolver = this.participantInitialResolver();
    if (resolver) return resolver(discussion);

    const name = this.getParticipantName(discussion);
    return name.charAt(0).toUpperCase();
  }

  isDiscussionStarred(discussion: DiscussionDto): boolean {
    const userId = this.userStore.current()?._id;
    const p = discussion.participants.find(
      (part) => userId && part.participant.toString() === userId.toString(),
    );
    return p?.isStarred || false;
  }

  isDiscussionArchived(discussion: DiscussionDto): boolean {
    const userId = this.userStore.current()?._id;
    const p = discussion.participants.find(
      (part) => userId && part.participant.toString() === userId.toString(),
    );
    return p?.isArchived || false;
  }

  getUnreadCount(discussion: DiscussionDto): number {
    const userId = this.userStore.current()?._id;
    const p = discussion.participants.find(
      (part) => userId && part.participant.toString() === userId.toString(),
    );
    return p?.unreadCount || 0;
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (atBottom) {
      this.scrolledToBottom.emit();
    }
  }
}
